/* eslint-disable max-lines */
import EventBus from "common/eventbus";
import Events from "common/events";
import { api } from "jiffy-api";
import { track } from "jiffy-analytics";
import { redirectTo } from "common/utils/browser";
import { startMixpanelRecording } from "common/utils/mixpanel/tracker";
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import { validateArtworkFileType } from "common/artwork-file-types";
import "./design-upload-cta.scss";

const instances = new Map();

class DesignUploadCTA {
  constructor(container) {
    this.container = container;
    this.dropZone = container.querySelector(".border-dashed");
    this.myDeviceBtn = container.querySelector(".js-my-device-btn");
    this.myLibraryBtn = container.querySelector(".js-my-library-btn");
    this.weaverAiStudioBtn = container.querySelector(".js-weaver-ai-studio-btn");
    this.canvaBtn = container.querySelector(".js-canva-btn");
    this.fileInput = container.querySelector(".js-design-file-input");
    this.catalogNumber = container.dataset.catalogNumber;
    this.color = container.dataset.color;

    // this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleMyDeviceClick = this.handleMyDeviceClick.bind(this);
    this.handleMyLibraryClick = this.handleMyLibraryClick.bind(this);
    this.handleCanvaClick = this.handleCanvaClick.bind(this);
    this.handleWeaverAiStudioClick = this.handleWeaverAiStudioClick.bind(this);
  }

  init() {
    this.attachEventListeners();
    waitForWindowVariable("mixpanel", () => {
      startMixpanelRecording(100);
    });

    EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (evt, { color }) => {
      this.handleDataUpdate({
        catalogNumber: this.catalogNumber,
        color
      });
    });
  }

  attachEventListeners() {
    this.attachDropZoneEventListeners();
    this.attachButtonEventListeners();
  }

  static preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDataUpdate({ catalogNumber, color }) {
    if (!catalogNumber || !color) return;

    this.catalogNumber = catalogNumber;
    this.color = color;

    if (this.myLibraryBtn) {
      const currentUrl = new URL(this.myLibraryBtn.href);
      currentUrl.searchParams.set("ac", color);
      this.myLibraryBtn.href = currentUrl.toString();
    }

    if (this.canvaBtn) {
      const currentUrl = new URL(this.canvaBtn.href);
      currentUrl.searchParams.set("ac", color);
      this.canvaBtn.href = currentUrl.toString();
    }

    if (this.weaverAiStudioBtn) {
      const currentUrl = new URL(this.weaverAiStudioBtn.href);
      currentUrl.searchParams.set("ac", color);
      currentUrl.searchParams.set("product", catalogNumber);
      this.weaverAiStudioBtn.href = currentUrl.toString();
    }
  }

  handleDragEnter(e) {
    DesignUploadCTA.preventDefaults(e);
    this.dropZone.classList.add("border-blue-600", "bg-blue-50");
  }

  handleDragLeave(e) {
    DesignUploadCTA.preventDefaults(e);
    if (e.target === this.dropZone) {
      this.dropZone.classList.remove("border-blue-600", "bg-blue-50");
    }
  }

  handleDrop(e) {
    DesignUploadCTA.preventDefaults(e);
    this.dropZone.classList.remove("border-blue-600", "bg-blue-50");

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  handleMyDeviceClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this.triggerSource = this.myDeviceBtn.dataset.trigger || "upload_section";

    if (this.myDeviceBtn.dataset.trigger) {
      this.myDeviceBtn.removeAttribute("data-trigger");
    }

    this.fileInput.click();
  }

  handleMyLibraryClick(e) {
    e.preventDefault();

    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: this.color,
      source: "design_library",
      trigger: "upload_section"
    });

    redirectTo(this.myLibraryBtn.href);
  }

  handleCanvaClick(e) {
    e.preventDefault();

    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: this.color,
      source: "canva",
      trigger: "upload_section"
    });

    redirectTo(this.canvaBtn.href);
  }

  handleWeaverAiStudioClick(e) {
    e.preventDefault();

    track("ai_studio_cta_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: this.color,
      referer: "design_upload_cta"
    });

    redirectTo(this.weaverAiStudioBtn.href);
  }

  handleFileSelect(e) {
    const { files } = e.target;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
    // Reset input
    e.target.value = "";
  }

  // File type validation using shared artwork file types module
  static validateFileType(file) {
    return validateArtworkFileType(file);
  }

  processFile(file) {
    // Validate file type before processing
    const { valid, message } = DesignUploadCTA.validateFileType(file);

    if (!valid) {
      this.showError(message);
      return;
    }

    this.showUploadProgress(file.name);
    this.uploadFile(file);
  }

  showUploadProgress(fileName) {
    this.originalContent = this.dropZone.innerHTML;
    this.dropZone.innerHTML = `
      <div class="text-center py-4">
        <p class="text-lg font-semibold mb-2">Uploading ${fileName}...</p>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div class="js-progress-bar bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
        </div>
      </div>
    `;
  }

  async uploadFile(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("generate_link", true);
    formData.append("is_made", true);
    formData.append("is_made_flash", false);

    if (this.catalogNumber) {
      formData.append("catalog_number", this.catalogNumber);
    }
    if (this.color) {
      formData.append("ac", this.color);
    }

    try {
      const response = await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        onUploadProgress: ({ progress }) => {
          this.updateProgress(progress * 100);
        }
      });

      if (response.redirect_url) {
        track("start_designing_clicked", {
          catalog_number: this.catalogNumber,
          composition_name: this.color,
          source: "upload",
          trigger: this.triggerSource || "upload_section"
        });
        redirectTo(response.redirect_url);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Upload failed. Please try again.";
      this.showError(errorMessage);
    }
  }

  updateProgress(percentage) {
    const progressBar = this.dropZone.querySelector(".js-progress-bar");
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }
  }

  showError(message) {
    if (this.originalContent) {
      this.dropZone.innerHTML = this.originalContent;
    }

    const errorDiv = document.createElement("div");
    errorDiv.className =
      "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center";
    errorDiv.textContent = message;

    this.dropZone.insertAdjacentElement("beforebegin", errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  resetUploadState() {
    if (this.originalContent) {
      this.dropZone.innerHTML = this.originalContent;
      // Re-initialize element references after DOM restoration
      this.reinitializeElements();
    }
    if (this.fileInput) {
      this.fileInput.value = "";
    }
  }

  reinitializeElements() {
    // Update element references since DOM was restored
    this.myDeviceBtn = this.container.querySelector(".js-my-device-btn");
    this.myLibraryBtn = this.container.querySelector(".js-my-library-btn");
    this.canvaBtn = this.container.querySelector(".js-canva-btn");
    this.fileInput = this.container.querySelector(".js-design-file-input");

    // Re-attach event listeners only to elements that were replaced
    this.attachButtonEventListeners();
    this.attachDropZoneEventListeners();
  }

  attachButtonEventListeners() {
    // My Device button
    if (this.myDeviceBtn) {
      this.myDeviceBtn.addEventListener("click", this.handleMyDeviceClick);
    }

    // My Design Library button
    if (this.myLibraryBtn) {
      this.myLibraryBtn.addEventListener("click", this.handleMyLibraryClick);
    }

    // Canva button
    if (this.canvaBtn) {
      this.canvaBtn.addEventListener("click", this.handleCanvaClick);
    }

    // Weaver AI Studio button
    if (this.weaverAiStudioBtn) {
      this.weaverAiStudioBtn.addEventListener("click", this.handleWeaverAiStudioClick);
    }

    // File input
    if (this.fileInput) {
      this.fileInput.addEventListener("change", this.handleFileSelect);
    }
  }

  attachDropZoneEventListeners() {
    // Prevent default drag behaviors
    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.dropZone.addEventListener(eventName, DesignUploadCTA.preventDefaults, false);
    });

    // Highlight on drag
    this.dropZone.addEventListener("dragenter", this.handleDragEnter, false);
    this.dropZone.addEventListener("dragover", DesignUploadCTA.preventDefaults, false);

    ["dragleave", "drop"].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.handleDragLeave, false);
    });

    // Handle drop
    this.dropZone.addEventListener("drop", this.handleDrop, false);
  }
}

const initDesignUploadModal = () => {
  const containers = document.querySelectorAll(".design-upload-cta");

  containers.forEach(container => {
    if (!instances.has(container)) {
      const instance = new DesignUploadCTA(container);
      instance.init();
      instances.set(container, instance);
    }
  });
};

// eslint-disable-next-line max-lines-per-function
export const showDesignUploadModal = (isDynamic = false, modalData = {}) => {
  const $modal = document.getElementById("design-upload-modal");

  if (isDynamic) {
    const $designLibraryLink = $modal.querySelector(".js-my-library-btn");
    if ($designLibraryLink) $designLibraryLink.href = modalData.designLibraryUrl;

    const $canvaLink = $modal.querySelector(".js-canva-btn");
    if ($canvaLink) $canvaLink.href = modalData.canvaUrl;

    // Initialize if needed and get the modal's instance
    const modalContainer = $modal.querySelector(".design-upload-cta");
    const modalInstance = instances.get(modalContainer);
    if (modalInstance) {
      modalInstance.handleDataUpdate({
        catalogNumber: modalData.catalogNumber,
        color: modalData.color
      });
    }
  }

  if ($modal) {
    const startTime = Date.now();

    $($modal)
      .modal("show")
      .on("hidden.bs.modal", () => {
        const duration = Date.now() - startTime;

        track("upload_step_abandoned", {
          product_id: modalData.productId,
          trigger_location: modalData.isFeatured ? "Top Tile (G640)" : "Product Card",
          abandoned: true,
          time_on_step: `${Math.round(duration / 1000)}s`
        });
      });
  }

  window.addEventListener(
    "pageshow",
    e => {
      if (e.persisted) {
        setTimeout(() => {
          $($modal).modal("hide");
          const modalContainer = $modal.querySelector(".design-upload-cta");
          const modalInstance = instances.get(modalContainer);
          if (modalInstance) {
            modalInstance.resetUploadState();
          }
        });
      }
    },
    false
  );
};

export default initDesignUploadModal;
